import React from 'react'
import { AceEditorInstance, CodeAnnotation, CodeMarker, CodeTextEditor } from '../code-text-editor'
import { CodeWidgetAnalysis } from '../widgets/CodeWidget/analysis/code-widget-analysis'
import { STATUS_STYLE, Tone, TONE_RANK } from './status-style'

type LineReference = { tone: Tone; label: string }

/**
 * Every line the report points at, and what it points at it for.
 *
 * Findings, host calls and outbound requests all carry line numbers already; this inverts them so the
 * source is annotated from the same data the left column is written from. A finding the report shows as
 * "Confirm" is tinted as one here too.
 */
export function buildLineIndex(widget: CodeWidgetAnalysis, reviewIds: Set<string>): Map<number, LineReference[]> {
  const index = new Map<number, LineReference[]>()
  const add = (line: number, reference: LineReference) => {
    const existing = index.get(line)
    if (existing) existing.push(reference)
    else index.set(line, [reference])
  }

  for (const finding of widget.findings) {
    const tone = reviewIds.has(finding.id) ? 'review' : finding.severity
    for (const item of finding.evidence) add(item.line, { tone, label: finding.title })
  }
  for (const entry of widget.bridgeUsage) {
    for (const item of entry.evidence) add(item.line, { tone: 'info', label: `wistor.${entry.member}` })
  }
  for (const target of widget.egress) {
    add(target.evidence.line, { tone: 'info', label: target.url ?? `${target.kind}, target built at runtime` })
  }

  return index
}

function worstOf(references: LineReference[]): Tone {
  return references.reduce<Tone>(
    (worst, reference) => (TONE_RANK.indexOf(reference.tone) < TONE_RANK.indexOf(worst) ? reference.tone : worst),
    'info'
  )
}

const ACE_ANNOTATION_TYPE: Record<Tone, CodeAnnotation['type']> = {
  high: 'error',
  medium: 'warning',
  review: 'warning',
  low: 'info',
  info: 'info',
}

/**
 * The page, read-only, in the same editor the builder edits it in, with a gutter whose line numbers are the
 * ones the evidence on the left refers to. An icon per referenced row with the reasons on hover, the row
 * tinted by the worst thing found on it, and the row jumped to marked over the top. Clicking the gutter goes
 * back the other way.
 */
export function PageSource(props: {
  html: string
  name: string
  lineIndex: Map<number, LineReference[]>
  activeLine: number | null
  onEditorLoad: (editor: AceEditorInstance) => void
  onGutterClick: (row: number) => void
}) {
  const { lineIndex, activeLine } = props

  const annotations = React.useMemo<CodeAnnotation[]>(
    () =>
      Array.from(lineIndex.entries()).map(([line, references]) => ({
        row: line - 1,
        column: 0,
        type: ACE_ANNOTATION_TYPE[worstOf(references)],
        text: references.map((reference) => reference.label).join('\n'),
      })),
    [lineIndex]
  )

  const markers = React.useMemo<CodeMarker[]>(() => {
    const fullLine = (line: number, className: string): CodeMarker => ({
      startRow: line - 1,
      startCol: 0,
      endRow: line - 1,
      endCol: 1,
      className,
      type: 'fullLine',
    })
    const rows = Array.from(lineIndex.entries()).map(([line, references]) =>
      fullLine(line, STATUS_STYLE.tone[worstOf(references)].marker)
    )
    return activeLine == null ? rows : [...rows, fullLine(activeLine, STATUS_STYLE.activeMarker)]
  }, [lineIndex, activeLine])

  return (
    <CodeTextEditor
      readonly
      name={`diagnostics-source-${props.name}`}
      value={props.html}
      mode="html"
      fileExtension="html"
      mimeType="text/html"
      exportFileName={`${props.name}.html`}
      collapsedHeight="60vh"
      annotations={annotations}
      markers={markers}
      onEditorLoad={props.onEditorLoad}
      onGutterClick={props.onGutterClick}
    />
  )
}
